/* import __COLOCATED_TEMPLATE__ from './dependency-warning-modal.hbs'; */
/* RESPONSIBLE TEAM: team-inbox */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type Router from '@ember/routing/router-service';
import { action } from '@ember/object';
import type { Brand } from './types/brand';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type { DependencyData } from './sidebar';

interface Args {
  brand: Brand;
  dependencyData: DependencyData;
  isFetchingDependentObjects: boolean;
  isDeleting: boolean;
  onDelete: () => void;
  onClose: () => void;
}

interface Signature {
  Args: Args;
}

const MAX_DISPLAYED = 5;

export default class DependencyWarningModal extends Component<Signature> {
  @service declare router: Router;

  get emailAddresses() {
    return this.args.dependencyData.dependent_email_addresses || [];
  }

  get messengerStyles() {
    return this.args.dependencyData.dependent_messenger_styles || [];
  }

  // TODO: Remove once mbf-sms-brand-phone-number-picker is GA — phoneNumbers replaces this
  get callingPhoneNumbers() {
    return this.args.dependencyData.dependent_calling_phone_numbers || [];
  }

  get phoneNumbers() {
    return this.args.dependencyData.dependent_phone_numbers || [];
  }

  get allPhoneNumberDeps() {
    // Use new field if available, fall back to old field during migration
    return this.phoneNumbers.length > 0 ? this.phoneNumbers : this.callingPhoneNumbers;
  }

  get isSafeToDelete() {
    return (
      this.emailAddresses.length === 0 &&
      this.messengerStyles.length === 0 &&
      this.allPhoneNumberDeps.length === 0
    );
  }

  get displayedEmailAddresses() {
    if (this.emailAddresses.length <= MAX_DISPLAYED) {
      return this.emailAddresses;
    }

    return this.emailAddresses.slice(0, MAX_DISPLAYED);
  }

  get remainingEmailAddressCount() {
    return Math.max(0, this.emailAddresses.length - MAX_DISPLAYED);
  }

  get displayedMessengerStyles() {
    if (this.messengerStyles.length <= MAX_DISPLAYED) {
      return this.messengerStyles;
    }

    return this.messengerStyles.slice(0, MAX_DISPLAYED);
  }

  get remainingMessengerStylesCount() {
    return Math.max(0, this.messengerStyles.length - MAX_DISPLAYED);
  }

  get displayedCallingPhoneNumbers() {
    if (this.callingPhoneNumbers.length <= MAX_DISPLAYED) {
      return this.callingPhoneNumbers;
    }

    return this.callingPhoneNumbers.slice(0, MAX_DISPLAYED);
  }

  get remainingCallingPhoneNumbersCount() {
    return Math.max(0, this.callingPhoneNumbers.length - MAX_DISPLAYED);
  }

  get displayedPhoneNumbers() {
    if (this.phoneNumbers.length <= MAX_DISPLAYED) {
      return this.phoneNumbers;
    }
    return this.phoneNumbers.slice(0, MAX_DISPLAYED);
  }

  get remainingPhoneNumbersCount() {
    return Math.max(0, this.phoneNumbers.length - MAX_DISPLAYED);
  }

  get hasAnyDependencies() {
    return !this.isSafeToDelete;
  }

  get dependencyColumnCount() {
    let count = 0;
    if (this.emailAddresses.length > 0) {
      count++;
    }
    if (this.messengerStyles.length > 0) {
      count++;
    }
    if (this.phoneNumbers.length > 0) {
      count++;
    }
    return count;
  }

  get gridColsClass() {
    if (this.dependencyColumnCount >= 3) {
      return 'grid-cols-3';
    }
    if (this.dependencyColumnCount === 2) {
      return 'grid-cols-2';
    }
    return 'grid-cols-1';
  }

  @action openCallingSettingsInNewWindow() {
    let actionsRoute = 'apps.app.settings.calling';
    let url: string = this.router.urlFor(actionsRoute);
    safeWindowOpen(url);
  }

  @action openEmailAddressesInNewWindow() {
    let actionsRoute = 'apps.app.settings.channels.email';
    let url: string = this.router.urlFor(actionsRoute, {
      queryParams: {
        tab: 'addresses',
      },
    });
    safeWindowOpen(url);
  }

  @action openMessengerStylesInNewWindow() {
    let actionsRoute = 'apps.app.settings.channels.messenger.web';
    let url: string = this.router.urlFor(actionsRoute, {
      queryParams: {
        section: 'general',
        tab: 'styling',
      },
    });
    safeWindowOpen(url);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Brands::DependencyWarningModal': typeof DependencyWarningModal;
    'new-settings/workspace/brands/dependency-warning-modal': typeof DependencyWarningModal;
  }
}
